import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { ACLService } from '@delon/acl';
import {
  ITokenService,
  DA_SERVICE_TOKEN,
  ITokenModel,
  ALLOW_ANONYMOUS
} from '@delon/auth';
import { MenuService, SettingsService } from '@delon/theme';
import { NzModalService } from 'ng-zorro-antd/modal';
import { UserDefaultService } from 'src/app/shared/services/user-default.service';

import { EventListener } from '../interfaces/listener';
import { FcwidgetService } from './fcwidget.service';

declare global {
  interface Window {
    $: any;
  }
}

@Injectable()
export class AuthService {
  private readonly onSetToken = new EventListener<ITokenModel | null>();
  constructor(
    private http: HttpClient,
    @Inject(DA_SERVICE_TOKEN) public tokenService: ITokenService,
    private settings: SettingsService,
    private aclService: ACLService,
    private menuService: MenuService,
    private router: Router,
    private reuseTabService: ReuseTabService,
    private modalSrv: NzModalService,
    private fcWidget: FcwidgetService,
    private udService: UserDefaultService
  ) {}

  login(user: any) {
    return this.http.post('api/auth/token', user, {
      context: new HttpContext().set(ALLOW_ANONYMOUS, true)
    });
  }

  openCompany(tid: string) {
    this.switchCompany(tid).then(
      () => {
        setTimeout(() => {
          this.router.navigate(['']).then(() => {
            this.muteLeaveAppAlert();
            window.location.reload();
          });
        }, 500);
      },
      () => {}
    );
  }

  private muteLeaveAppAlert() {
    if (!window.app) window.app = {};
    window.app.forcereload = true;
  }

  switchCompany(tenantId: string) {
    return new Promise((resolve, reject) => {
      this.http
        .post('api/Companies/SwitchCompany?_silent=true', {
          tenantId: tenantId
        })
        .subscribe({
          next: data => {
            this.setToken(data);
            // clear resue tabs
            this.reuseTabService.clear();
            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  }

  sendpassword(user: any) {
    return this.http.post('api/user/sendpassword', user, {
      context: new HttpContext().set(ALLOW_ANONYMOUS, true)
    });
  }

  resetpassword(user: any) {
    return this.http.post('api/user/resetpassword', user, {
      context: new HttpContext().set(ALLOW_ANONYMOUS, true)
    });
  }

  register(user: any) {
    return this.http.post('api/user/register', user, {
      context: new HttpContext().set(ALLOW_ANONYMOUS, true)
    });
  }

  refreshToken() {
    return new Promise((resolve, reject) => {
      this.http.post('api/auth/refresh?_silent=true', null).subscribe({
        next: (token: any) => {
          this.setToken(token);
          resolve(true);
        },
        error: () => {
          reject(false);
        }
      });
    });
  }

  setToken(jwt: any) {
    if (jwt && jwt.expired && jwt.token) {
      this.setJwtToken(jwt);
      //this.setPermissions(data.abilities);
      // this.menuService.menus.forEach((m: any) => {
      //   this.updateSubMenuHideProperty(m);
      // });
      this.menuService.resume();
    }
  }

  private setJwtToken(jwt: any) {
    const app: any = this.settings.app;
    jwt['appName'] = app.name;
    // Don't overwrite company info from JWT if it exists (for company switching)
    if (!jwt.company) {
      jwt.company = this.settings.user['company'];
    }

    app.dbType = jwt.dbType;
    app.licensed = jwt.licensed;
    this.tokenService.set(jwt);
    this.setUserInfo(jwt);
    // if (ajaxSetup) {
    //   ajaxSetup.ajaxSettings = {
    //     headers: { Authorization: `Bearer ${jwt.token}` }
    //   };
    // }
    if (window.$)
      window.$.ajaxSetup({
        beforeSend: function (xhr: any) {
          xhr.setRequestHeader('Authorization', `Bearer ${jwt.token}`);
        }
      });
    this.onSetToken.next(jwt);
  }

  get tokenChanged() {
    return this.onSetToken.self();
  }

  logout() {
    this.modalSrv.confirm({
      nzTitle: 'Logout?',
      nzContent: 'Are you sure you want to logout?',
      nzOnOk: () => {
        this.http
          .post('api/auth/logout', {}, { params: { _silent: true } }) //{} is empty object
          .subscribe(() => {
            try {
              if (this.fcWidget.isOpen()) {
                this.fcWidget.close();
              }
            } catch (error) {
              console.warn('Error closing fcWidget:', error);
            }

            this.tokenService.clear();
            this.settings.setUser({});
            this.aclService.setAbility([]);
            this.menuService.resume();
            this.onSetToken.next(null);
            this.udService.clearDefaultSettingsCache();
            this.router.navigateByUrl(this.tokenService.login_url!).then(() => {
              window.location.reload();
            });
          });
      }
    });
  }

  private setUserInfo(token: any) {
    const user = { ...token };
    // user info
    this.settings.setUser(user);
  }
}
